import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  clampHour12,
  clampMinuteOrSecond,
  dateToParts,
  DEFAULT_TIME_PARTS,
  EMPTY_DATETIME_PARTS,
  formatDateTimeForInput,
  mergeTimePartsIntoDate,
  normalizeMeridiem,
  pad2,
  parseDateTimeInput,
  partsToDate,
  type DateTimeParts,
} from '../utils/datetimeFormat';

export type DateTimeEditMode = 'segments' | 'fullString';

export interface UseDateTimeInputOptions {
  value: Date | null;
  showSeconds?: boolean;
  onChange?: (date: Date | null) => void;
}

export const useDateTimeInput = ({
  value,
  showSeconds = true,
  onChange,
}: UseDateTimeInputOptions) => {
  const [parts, setParts] = useState<DateTimeParts>(() =>
    value ? dateToParts(value, showSeconds) : { ...EMPTY_DATETIME_PARTS }
  );
  const [editMode, setEditMode] = useState<DateTimeEditMode>('segments');
  const [fullStringValue, setFullStringValue] = useState('');

  useEffect(() => {
    if (value && !Number.isNaN(value.getTime())) {
      setParts(dateToParts(value, showSeconds));
      setFullStringValue(formatDateTimeForInput(value, showSeconds));
    } else if (value === null) {
      setParts({ ...EMPTY_DATETIME_PARTS });
      setFullStringValue('');
    }
  }, [value, showSeconds]);

  const emitIfComplete = useCallback(
    (nextParts: DateTimeParts) => {
      const date = partsToDate(nextParts, showSeconds);
      if (date) {
        onChange?.(date);
      }
    },
    [onChange, showSeconds]
  );

  const applyParsedValue = useCallback(
    (input: string, existingDate: Date | null) => {
      const parsed = parseDateTimeInput(input);
      if (parsed.kind === 'full' || parsed.kind === 'date-only') {
        setParts(dateToParts(parsed.date, showSeconds));
        setFullStringValue(formatDateTimeForInput(parsed.date, showSeconds));
        onChange?.(parsed.date);
        return true;
      }
      if (parsed.kind === 'time-only') {
        const merged = mergeTimePartsIntoDate(existingDate, parsed.time, showSeconds);
        if (merged) {
          setParts(dateToParts(merged, showSeconds));
          setFullStringValue(formatDateTimeForInput(merged, showSeconds));
          onChange?.(merged);
          return true;
        }
      }
      return false;
    },
    [onChange, showSeconds]
  );

  const updatePart = useCallback(
    (key: keyof DateTimeParts, raw: string) => {
      setParts((prev) => {
        const next = { ...prev, [key]: raw };
        return next;
      });
    },
    []
  );

  const commitPart = useCallback(
    (key: keyof DateTimeParts) => {
      setParts((prev) => {
        const next = { ...prev };
        if (key === 'hour12') {
          const n = clampHour12(parseInt(prev.hour12 || DEFAULT_TIME_PARTS.hour12, 10));
          next.hour12 = pad2(n);
        } else if (key === 'minute' || key === 'second') {
          const n = clampMinuteOrSecond(parseInt(prev[key] || '0', 10));
          next[key] = pad2(n);
        } else if (key === 'day' || key === 'month') {
          next[key] = pad2(parseInt(prev[key] || '0', 10));
        } else if (key === 'meridiem') {
          next.meridiem = normalizeMeridiem(prev.meridiem);
        }
        const date = partsToDate(next, showSeconds);
        if (date) {
          onChange?.(date);
          setFullStringValue(formatDateTimeForInput(date, showSeconds));
        }
        return next;
      });
    },
    [onChange, showSeconds]
  );

  const handlePaste = useCallback(
    (text: string) => {
      applyParsedValue(text, value);
    },
    [applyParsedValue, value]
  );

  const enterFullStringMode = useCallback(() => {
    setFullStringValue(formatDateTimeForInput(value, showSeconds));
    setEditMode('fullString');
  }, [value, showSeconds]);

  const commitFullString = useCallback(() => {
    const ok = applyParsedValue(fullStringValue, value);
    if (!ok && fullStringValue.trim() === '') {
      setParts({ ...EMPTY_DATETIME_PARTS });
      onChange?.(null);
    }
    setEditMode('segments');
  }, [applyParsedValue, fullStringValue, onChange, value]);

  const toggleMeridiem = useCallback(() => {
    setParts((prev) => {
      const next: DateTimeParts = {
        ...prev,
        meridiem: prev.meridiem === 'AM' ? 'PM' : 'AM',
      };
      emitIfComplete(next);
      return next;
    });
  }, [emitIfComplete]);

  const setMeridiem = useCallback(
    (meridiem: DateTimeParts['meridiem']) => {
      setParts((prev) => {
        const next: DateTimeParts = { ...prev, meridiem };
        emitIfComplete(next);
        return next;
      });
    },
    [emitIfComplete]
  );

  const displayValue = useMemo(
    () => formatDateTimeForInput(partsToDate(parts, showSeconds), showSeconds),
    [parts, showSeconds]
  );

  return {
    parts,
    editMode,
    fullStringValue,
    displayValue,
    setEditMode,
    setFullStringValue,
    updatePart,
    commitPart,
    handlePaste,
    enterFullStringMode,
    commitFullString,
    toggleMeridiem,
    setMeridiem,
    setPartsFromDate: (date: Date | null) => {
      if (date) {
        setParts(dateToParts(date, showSeconds));
        setFullStringValue(formatDateTimeForInput(date, showSeconds));
      }
    },
  };
};
